import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast as sonner } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { API, DrawerLabelCtx, PortalProvider, initials, useHeaders, usePortal } from '../clientportal/kit';
import { NotificationsPanel } from '../clientportal/NotificationsPanel';
import '../../styles/cleaner-portal.css';
import '../../styles/cleaner-portal-app.css';

export const GURU_NAV = [
  ['overview', 'Today', '◫', '/cleaner/dashboard'], ['schedule', 'My Schedule', '▦', '/cleaner/schedule'], ['jobs', 'My Jobs', '✓', '/cleaner/jobs'],
  ['messages', 'Team Messages', '▱', '/cleaner/messages'], ['time', 'My Time', '◷', '/cleaner/time'], ['payouts', 'My Payouts', '▤', '/cleaner/payouts'],
  ['availability', 'Availability', '▧', '/cleaner/availability'], ['account', 'My Account', '◎', '/cleaner/account'],
];
const MATCH = { jobs: ['/cleaner/job/', '/cleaner/performance'] };
const activeKey = (path) => (GURU_NAV.find(([k, , , to]) => path.startsWith(to) || (MATCH[k] || []).some((m) => path.startsWith(m))) || GURU_NAV[0])[0];
const GURU_HEAD = '<image href="/email-assets/guru-head-bold-v2.png" width="3500" height="3500"/>';

const Avatar = ({ user, light }) => (
  <span className={`avatar${light ? ' light' : ''}`}>{user?.photo_url ? <img src={user.photo_url} alt="Your profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : initials(user?.full_name)}</span>
);

const Switch = () => {
  const { user, canSwitchViews, switchView } = useAuth();
  const { pathname } = useLocation();
  if (!canSwitchViews) return null;
  const toAdmin = async () => {
    try { sessionStorage.setItem('gg_return_cleaner', pathname); await switchView('admin'); window.location.assign(sessionStorage.getItem('gg_return_admin') || '/admin/dashboard'); }
    catch (e) { sonner.error(e.response?.data?.detail || 'Could not switch views'); }
  };
  return (
    <nav className="workspace-switch" aria-label="Workspace view" data-testid="view-switch">
      {(user.available_views || []).includes('admin') && <button type="button" onClick={toAdmin} aria-label="Switch to Admin view" data-testid="view-switch-admin">Admin</button>}
      <span aria-current="page" data-testid="view-switch-cleaner">Cleaner</span>
    </nav>
  );
};

const Bell = ({ dot }) => {
  const { openDrawer } = usePortal();
  return (
    <button className="notification" aria-label="Notifications" data-testid="guru-notifications-btn"
      onClick={() => openDrawer({ title: 'Your work updates', content: <NotificationsPanel /> })}>
      <svg viewBox="330 260 3027 2963" aria-hidden="true" dangerouslySetInnerHTML={{ __html: GURU_HEAD }} />
      {dot && <i id="notification-dot" data-testid="guru-notification-dot" />}
    </button>
  );
};

// Approved Guru workspace frame (cleaner-portal-review-v1)
export const CleanerShell = ({ children }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const headers = useHeaders();
  const [open, setOpen] = useState(false);
  const [dot, setDot] = useState(false);
  const key = activeKey(pathname);
  const label = GURU_NAV.find((n) => n[0] === key)[1];
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); document.title = `${label} | Gleam Guru Workspace`; }, [pathname, label]);
  useEffect(() => {
    Promise.all([axios.get(`${API}/api/notifications/unread-count`, { headers }), axios.get(`${API}/api/portal/messages/unread`, { headers })])
      .then(([n, m]) => setDot((n.data.unread_count || 0) + (m.data.unread || 0) > 0)).catch(() => {});
  }, [headers, pathname]);
  return (
    <div className={`gcw${open ? ' nav-open' : ''}`} data-testid="cleaner-shell">
      <DrawerLabelCtx.Provider value="GURU WORKSPACE">
        <PortalProvider>
          {open && <button id="scrim" aria-label="Close navigation" onClick={() => setOpen(false)} data-testid="guru-scrim" />}
          <aside id="sidebar">
            <Link className="brand" to="/cleaner/dashboard" aria-label="Guru overview"><img src="/email-assets/gleam-guru-logo.png" alt="Gleam Guru Cleaners" /></Link>
            <div className="workspace-label"><i /> YOUR GURU WORKSPACE</div>
            <nav aria-label="Guru navigation">
              {GURU_NAV.map(([k, l, icon, to]) => (
                <Link key={k} to={to} className={`nav${k === key ? ' active' : ''}`} aria-current={k === key ? 'page' : undefined} data-testid={`guru-nav-${k}`}><span>{icon}</span>{l}</Link>
              ))}
            </nav>
            <div className="side-bottom">
              <Link to="/cleaner/messages" className="help" data-testid="guru-help">Need a hand? <span>Talk with your team ↗</span></Link>
              <div className="identity"><Avatar user={user} /><div><strong data-testid="guru-sidebar-name">{user?.full_name}</strong><small>{user?.role === 'cleaner' ? 'Gleam Guru' : 'Team member'}</small></div></div>
            </div>
          </aside>
          <div className="shell">
            <header>
              <div className="breadcrumb">
                <button id="menu" aria-label="Open navigation" aria-expanded={open} onClick={() => setOpen(true)} data-testid="guru-menu-btn">☰</button>
                <span>My workspace</span><span>/</span><strong data-testid="guru-crumb">{label}</strong>
              </div>
              <div className="top-actions">
                <Switch />
                <Bell dot={dot} />
                <Link to="/cleaner/account" aria-label="My account" style={{ textDecoration: 'none' }} data-testid="guru-topbar-account"><Avatar user={user} light /></Link>
              </div>
            </header>
            <main id="content" tabIndex={-1}><div className="page-enter" key={pathname}>{children}</div></main>
            <footer>GLEAM GURU CLEANERS <span>Your Guru workspace · Times shown in Pacific time</span></footer>
          </div>
        </PortalProvider>
      </DrawerLabelCtx.Provider>
    </div>
  );
};

export default CleanerShell;
